import { isQubictronDevHostname, isQubictronLocHostname, QubictronBackofficeHostnames, qubictronBackofficeUrlMap, qubictronUrlMap } from "../shared/url.utils";
import { UrlBuilder } from "../shared/urlBuilder";
import { QubictronAppsUrlBuilder, QubictronUrlBuilder } from "./urlBuilder.client";

/**
 * A utility class for managing and building asset URLs for Qubictron applications.
 * Provides methods to construct URLs for various asset types including JavaScript bundles,
 * CSS files, localization files, and other static resources.
 *
 * @example
 * ```typescript
 * const assets = new Assets({
 *   appFolder: 'my-app',
 *   hash: 'v1.2.3',
 *   devHosts: ['localhost:3000', 'dev.example.com'],
 *   originResolverCallbackMap: {
 *     dev: () => ({ url: 'https://dev.example.com' }),
 *     prod: () => ({ url: 'https://prod.example.com' })
 *   }
 * });
 *
 * // Get JavaScript bundle URL
 * const jsBundle = assets.js.bundleBuilder().build();
 *
 * // Get CSS bundle URL
 * const cssBundle = assets.css.bundleBuilder().build();
 * ```
 */
export class Assets {
  appsDir = "apps";

  /** The application folder name used in URL paths */

  /** Array of development hostnames for environment detection */

  /** Optional callback map for custom URL resolution based on environment */

  /** Hash string used for cache busting in asset URLs */

  constructor(options) {
    this.appFolder = options.appFolder;
    this.devHosts = options.devHosts ?? [];
    this.originResolverCallbackMap = options.originResolverCallbackMap;
    this.hash = options.hash ?? "";
    this.baseUrl = options.baseUrl;
  }
  basePathBuilder() {
    const builder = new QubictronAppsUrlBuilder({
      url: this.baseUrl,
      originResolverCallbackMap: this.originResolverCallbackMap
    });
    builder.setPathname("");
    builder.setSearch(this.hash);
    if (this.devHosts.length) {
      this.devHosts.forEach(host => builder.addDevHostname(host));
    }
    return builder;
  }
  appsPathBuilder() {
    return this.basePathBuilder().appendPathname(this.appsDir).appendPathname(this.appFolder);
  }
  packageAsAppPathBuilder() {
    return this.basePathBuilder().appendPathname(this.appFolder);
  }
  get locales() {
    const localesPathUrlBuilder = () => this.appsPathBuilder().appendPathname("locales");
    return {
      pathBuilder: localesPathUrlBuilder(),
      translationFileBuilder: () => localesPathUrlBuilder().appendPathname("{{lng}}.json").decode()
    };
  }
  get js() {
    const jsPathBuilder = () => this.appsPathBuilder().appendPathname("js");
    return {
      pathBuilder: jsPathBuilder(),
      bundleBuilder: () => {
        return jsPathBuilder().appendPathname(jsPathBuilder().isDevHost() ? "bundle.dev.js" : "bundle.js.gz");
      }
    };
  }
  get css() {
    const packageAsAppBuilder = () => this.packageAsAppPathBuilder().appendPathname("css");
    const appsCssPathBuilder = () => this.appsPathBuilder().appendPathname("css");
    const fontsCssPathBuilder = () => this.basePathBuilder().appendPathname("fonts/css");
    const fontsFontAwesomeCssPathBuilder = () => fontsCssPathBuilder().appendPathname("fontawesome");
    return {
      appPathBuilder: appsCssPathBuilder(),
      appBundleBuilder: ({
        isPackageAsApp = false
      } = {}) => {
        if (isPackageAsApp) {
          return packageAsAppBuilder().appendPathname(appsCssPathBuilder().isDevHost() ? "bundle.css" : "bundle.css.gz");
        }
        return appsCssPathBuilder().appendPathname(appsCssPathBuilder().isDevHost() ? "bundle.dev.css" : "bundle.css.gz");
      },
      fonts: {
        pathBuilder: fontsCssPathBuilder(),
        bundleBuilder: () => {
          const builder = fontsCssPathBuilder();
          return builder.appendPathname(builder.isDevHost() ? "fonts.css" : "fonts.css.gz");
        },
        bundleRegisterBuilder: () => {
          const builder = fontsCssPathBuilder();
          return builder.appendPathname(builder.isDevHost() ? "fonts-register.css" : "fonts-register.css.gz");
        },
        montserratBundleRegisterBuilder: () => {
          const builder = fontsCssPathBuilder();
          return builder.appendPathname(builder.isDevHost() ? "montserrat-register.css" : "montserrat-register.css.gz");
        },
        fontawesomeBundleRegisterBuilder: () => {
          const builder = fontsFontAwesomeCssPathBuilder();
          return builder.appendPathname(builder.isDevHost() ? "fontawesome-register.css" : "fontawesome-register.css.gz");
        },
        fontawesomeBundleBuilder: () => {
          const builder = fontsFontAwesomeCssPathBuilder();
          return builder.appendPathname(builder.isDevHost() ? "fontawesome.css" : "fontawesome.css.gz");
        },
        fontawesomeBrandsBundleBuilder: () => {
          const builder = fontsFontAwesomeCssPathBuilder();
          return builder.appendPathname(builder.isDevHost() ? "brands.css" : "brands.css.gz");
        },
        fontawesomeDuotoneBundleBuilder: () => {
          const builder = fontsFontAwesomeCssPathBuilder();
          return builder.appendPathname(builder.isDevHost() ? "duotone.css" : "duotone.css.gz");
        },
        fontawesomeLightBundleBuilder: () => {
          const builder = fontsFontAwesomeCssPathBuilder();
          return builder.appendPathname(builder.isDevHost() ? "light.css" : "light.css.gz");
        },
        fontawesomeRegularBundleBuilder: () => {
          const builder = fontsFontAwesomeCssPathBuilder();
          return builder.appendPathname(builder.isDevHost() ? "regular.css" : "regular.css.gz");
        },
        fontawesomeSolidBundleBuilder: () => {
          const builder = fontsFontAwesomeCssPathBuilder();
          return builder.appendPathname(builder.isDevHost() ? "solid.css" : "solid.css.gz");
        },
        fontawesomeThinBundleBuilder: () => {
          const builder = fontsFontAwesomeCssPathBuilder();
          return builder.appendPathname(builder.isDevHost() ? "thin.css" : "thin.css.gz");
        }
      }
    };
  }
  get imagesAppPathBuilder() {
    return this.appsPathBuilder().appendPathname(`images`);
  }
  get svgAppPathlBuilder() {
    return this.appsPathBuilder().appendPathname(`images/svg`);
  }
}

/**
 * @description Origin of the back office API for the page currently running this bundle.
 *
 * The local `.loc` stack is published on whichever port the developer's Docker
 * setup exposes, so the protocol and port are taken from the live location
 * rather than assumed to be the defaults. The dev stack gets the dev back
 * office, and every other host gets production.
 */
export const resolveBackofficeOrigin = (location = window.location) => {
  if (isQubictronDevHostname(location.hostname)) {
    return qubictronBackofficeUrlMap.dev().origin;
  }
  if (!isQubictronLocHostname(location.hostname)) {
    return qubictronBackofficeUrlMap.prod().origin;
  }
  return new UrlBuilder(`${location.protocol}//${QubictronBackofficeHostnames.loc}`).setPort(location.port).origin;
};
export const QUBICTRON_SIGN_IN_URL = new QubictronUrlBuilder().setPathname("auth/sign-in").build();
export const QUBICTRON_SIGN_UP_URL = new QubictronUrlBuilder().setPathname("auth/sign-up").build();
export const createScacIconUrl = scac => {
  return qubictronUrlMap.prod().setPathname(`design/images/freight/sealine_cort/${scac.toUpperCase()}.jpg`).build();
};
export const createParcelCarrierIconUrl = scac => {
  return qubictronUrlMap.prod().setPathname(`design/images/freight/parcel/${scac.toUpperCase()}.png`).build();
};
export const createCountryFlagIconUrl = countryCode => {
  return new QubictronAppsUrlBuilder().setPathname(`icons/country-flags/${countryCode.toUpperCase()}.svg`).build();
};